'use client';

import { useCallback } from 'react';
import { Layers, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useSettings } from '@/providers/settings-provider';

const BG_OPTIONS = [
  {
    id: 'none',
    label: 'Kein',
    gradient: 'bg-gray-100 dark:bg-gray-800',
    dot: null,
  },
  {
    id: 'particles',
    label: 'Particle Mesh',
    gradient: 'bg-gradient-to-br from-cyan-500 to-violet-600',
    dot: '✦',
  },
  {
    id: 'aurora',
    label: 'Aurora',
    gradient: 'bg-gradient-to-br from-cyan-400 via-purple-500 to-emerald-400',
    dot: '◉',
  },
  {
    id: 'starfield',
    label: 'Starfield',
    gradient: 'bg-gradient-to-br from-slate-800 to-blue-950',
    dot: '★',
  },
  {
    id: 'orbs',
    label: 'Orbs',
    gradient: 'bg-gradient-to-br from-purple-500 to-blue-600',
    dot: '○',
  },
  {
    id: 'vortex',
    label: 'Vortex',
    gradient: 'bg-gradient-to-br from-cyan-500 via-violet-600 to-cyan-400',
    dot: '⊛',
  },
  {
    id: 'dna',
    label: 'DNA Helix',
    gradient: 'bg-gradient-to-br from-cyan-400 to-pink-500',
    dot: '⌘',
  },
  {
    id: 'crystals',
    label: 'Kristalle',
    gradient: 'bg-gradient-to-br from-violet-400 to-emerald-400',
    dot: '◈',
  },
  {
    id: 'hexgrid',
    label: 'Hex Grid',
    gradient: 'bg-gradient-to-br from-cyan-600 to-violet-700',
    dot: '⬡',
  },
  {
    id: 'grid',
    label: 'Grid Beams',
    gradient: 'bg-gradient-to-br from-slate-700 to-slate-900',
    dot: '▦',
  },
  {
    id: 'matrix',
    label: 'Matrix',
    gradient: 'bg-gradient-to-br from-black to-green-950',
    dot: '⌘',
  },
];

export function BackgroundPickerButton() {
  const { settings, storeOption } = useSettings();
  const current = settings?.ui?.backgroundAnimation ?? 'particles';

  const select = useCallback((id) => {
    storeOption('ui.backgroundAnimation', id);
  }, [storeOption]);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          mode="icon"
          shape="circle"
          className="size-9 hover:bg-primary/10 hover:[&_svg]:text-primary"
          title="Hintergrund wählen"
        >
          <Layers className="size-4.5!" />
        </Button>
      </PopoverTrigger>

      <PopoverContent
        side="bottom"
        align="end"
        className="w-64 p-3"
      >
        <p className="text-xs font-semibold text-muted-foreground mb-2 px-0.5">
          Hintergrund-Animation
        </p>

        <div className="grid grid-cols-2 gap-1.5">
          {BG_OPTIONS.map((opt) => {
            const isActive = current === opt.id;
            return (
              <button
                key={opt.id}
                onClick={() => select(opt.id)}
                className={`
                  relative flex items-center gap-2 px-2.5 py-2 rounded-lg text-left
                  transition-all duration-150 group
                  ${isActive
                    ? 'bg-primary/10 ring-1 ring-primary/30'
                    : 'hover:bg-muted'}
                `}
              >
                {/* Color swatch */}
                <div className={`
                  shrink-0 w-7 h-7 rounded-md flex items-center justify-center
                  text-white text-sm font-bold select-none
                  ${opt.gradient}
                `}>
                  {opt.dot && <span className="text-[11px] leading-none">{opt.dot}</span>}
                </div>

                {/* Label */}
                <span className={`text-xs font-medium leading-tight flex-1 ${isActive ? 'text-primary' : 'text-foreground'}`}>
                  {opt.label}
                </span>

                {/* Active checkmark */}
                {isActive && (
                  <Check className="size-3 text-primary shrink-0" />
                )}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
